import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useActiveOnScreen } from "@/lib/useActiveOnScreen";

/* LexGraph — matter intelligence graph.
   Centre node = the matter. Orbiting nodes = artifacts LexGraph threads together.
   Auto-rotates between three matter types to show breadth. */

const MATTERS = [
    {
        id: "rear-end",
        title: "Rear-end · I-95",
        client: "Hernandez, M.",
        chip: "Auto · PI",
        accent: "#1E5BFF",
        artifacts: [
            { id: "intake", icon: "fa-headset",        label: "Intake call",       meta: "06:42 · 11 min",    angle: -90 },
            { id: "police", icon: "fa-file-shield",    label: "Police report",     meta: "PDF · 4 pages",     angle: -30 },
            { id: "er",     icon: "fa-notes-medical",  label: "ER admit note",     meta: "Mercy Gen · L4-L5", angle: 30 },
            { id: "carrier",icon: "fa-building-shield",label: "Adjuster voicemail",meta: "GEICO · 02:14",     angle: 90 },
            { id: "photo",  icon: "fa-camera",         label: "Vehicle photos",    meta: "12 images",         angle: 150 },
            { id: "ret",    icon: "fa-file-signature", label: "Retainer signed",   meta: "DocuSign · today",  angle: -150 },
        ],
        insight: "LexGraph surfaced a prior soft-tissue claim from 2019 — flagged for adjuster strategy.",
    },
    {
        id: "wrongful",
        title: "Wrongful death · OB",
        client: "Wallace, K.",
        chip: "Med-mal · referred",
        accent: "#7C5BFF",
        artifacts: [
            { id: "ref",    icon: "fa-arrow-right-arrow-left", label: "Referral call",  meta: "From: Whitley", angle: -90 },
            { id: "chart",  icon: "fa-file-medical",   label: "Chart pull",          meta: "412 pp · indexed", angle: -30 },
            { id: "expert", icon: "fa-microscope",     label: "Expert lined up",     meta: "Dr. Reyes, OB",    angle: 30 },
            { id: "sol",    icon: "fa-hourglass-half", label: "SoL countdown",       meta: "184 days left",    angle: 90 },
            { id: "fam",    icon: "fa-user-group",     label: "Family interview",    meta: "Recorded · 38 m",  angle: 150 },
            { id: "demand", icon: "fa-file-lines",     label: "Demand draft",        meta: "Outline ready",    angle: -150 },
        ],
        insight: "LexGraph built a timeline of nine clinical events — chronology ready for the demand letter.",
    },
    {
        id: "estate",
        title: "Estate plan · revocable",
        client: "Tan family",
        chip: "Estate · multi-doc",
        accent: "#15803D",
        artifacts: [
            { id: "intake", icon: "fa-headset",        label: "Discovery call",   meta: "Both spouses",        angle: -90 },
            { id: "assets", icon: "fa-vault",          label: "Asset inventory",  meta: "9 accounts mapped",   angle: -30 },
            { id: "bene",   icon: "fa-people-roof",    label: "Beneficiaries",    meta: "4 named · 2 minors",  angle: 30 },
            { id: "trust",  icon: "fa-scroll",         label: "Trust draft",      meta: "v2 · review-ready",   angle: 90 },
            { id: "tax",    icon: "fa-calculator",     label: "Tax basis check",  meta: "Step-up flagged",     angle: 150 },
            { id: "sign",   icon: "fa-pen-fancy",      label: "Signing booked",   meta: "Apr 14 · 10 AM",      angle: -150 },
        ],
        insight: "LexGraph reconciled three account statements and pre-built the funding checklist.",
    },
];

const AtlasShowcase = () => {
    const [active, setActive] = useState(0);
    const { ref: sectionRef, active: onScreen } = useActiveOnScreen();

    useEffect(() => {
        if (!onScreen) return undefined;
        const t = setInterval(() => setActive((i) => (i + 1) % MATTERS.length), 7000);
        return () => clearInterval(t);
    }, [onScreen]);

    const matter = MATTERS[active];

    return (
        <section ref={sectionRef} data-testid="atlas-showcase" className="relative py-20 md:py-28 bg-[var(--cream)] overflow-hidden">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                {/* Copy */}
                <div className="lg:col-span-5 order-2 lg:order-1">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="eyebrow-pill">
                            <i className="fa-solid fa-folder-tree text-[10px] accent-blue" />
                            LexGraph · Matter intelligence
                        </span>
                        <h2 className="font-display text-[clamp(1.9rem,3.6vw,2.8rem)] leading-[1.05] tracking-tight font-semibold text-[var(--ink)] mt-5">
                            One matter. <span className="accent-blue">Every signal threaded.</span>
                        </h2>
                        <p className="mt-5 text-base md:text-lg text-[var(--ink-soft)] leading-relaxed">
                            LexGraph continuously reads every call, document, note, and inbound email tied to a matter — then answers any question your team asks, in plain English.
                        </p>
                        <ul className="mt-6 space-y-3 text-[14.5px] text-[var(--ink-soft)]">
                            {[
                                ["fa-bolt", "Live timeline auto-builds the day records arrive"],
                                ["fa-magnifying-glass", "Ask in plain English — \"What's the SoL on Hernandez?\""],
                                ["fa-link", "Cross-matter recall flags prior claims and conflicts"],
                                ["fa-shield-halved", "Source-grounded answers — every claim cites the doc"],
                            ].map(([icon, line]) => (
                                <li key={line} className="flex items-start gap-3">
                                    <span className="w-6 h-6 rounded-md bg-[var(--blue-soft)] accent-blue flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <i className={`fa-solid ${icon} text-[10px]`} />
                                    </span>
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>

                        {/* Insight callout */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={matter.id + "-insight"}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.4 }}
                                className="mt-7 rounded-xl border border-[var(--blue)]/30 bg-[rgba(30,91,255,0.05)] p-4"
                            >
                                <div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.16em] uppercase accent-blue mb-1.5">
                                    <i className="fa-solid fa-wand-magic-sparkles text-[10px]" />
                                    LexGraph insight
                                </div>
                                <p className="text-[13.5px] text-[var(--ink)] leading-snug">{matter.insight}</p>
                            </motion.div>
                        </AnimatePresence>
                    </motion.div>
                </div>

                {/* Visualization */}
                <div className="lg:col-span-7 order-1 lg:order-2">
                    {/* Mobile-only: clean stacked layout */}
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6 }}
                        className="md:hidden card-paper p-5 overflow-hidden"
                        data-testid="atlas-graph-mobile"
                    >
                        {/* tabs */}
                        <div className="flex items-center gap-1.5 overflow-x-auto pb-3 scrollbar-none -mx-1 px-1">
                            {MATTERS.map((m, i) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setActive(i)}
                                    className="flex-shrink-0 px-2.5 py-1 rounded-full font-mono text-[9.5px] tracking-[0.12em] uppercase transition-all"
                                    style={{
                                        background: active === i ? matter.accent : "var(--cream-2)",
                                        color: active === i ? "#fff" : "var(--ink-soft)",
                                    }}
                                >
                                    {m.chip}
                                </button>
                            ))}
                        </div>

                        {/* matter card */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={matter.id + "-m-centre"}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.4 }}
                                className="rounded-xl px-4 py-3 text-left shadow-[0_18px_40px_-22px_rgba(35,31,35,0.45)]"
                                style={{ background: matter.accent, color: "#fff" }}
                            >
                                <div className="font-mono text-[9.5px] tracking-[0.16em] uppercase opacity-80">Matter</div>
                                <div className="font-display font-semibold text-[15px] leading-tight mt-1">{matter.title}</div>
                                <div className="font-mono text-[10px] mt-1 opacity-85">{matter.client}</div>
                            </motion.div>
                        </AnimatePresence>

                        {/* artifacts list */}
                        <ul className="mt-4 grid grid-cols-2 gap-2">
                            {matter.artifacts.map((a, i) => (
                                <motion.li
                                    key={`${matter.id}-m-${a.id}`}
                                    initial={{ opacity: 0, x: -6 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.35, delay: 0.1 + i * 0.05 }}
                                    className="rounded-lg border border-[var(--border)] bg-[var(--paper)] p-2 flex items-center gap-2"
                                >
                                    <span
                                        className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                                        style={{ background: "var(--cream-2)", color: "var(--ink)" }}
                                    >
                                        <i className={`fa-solid ${a.icon} text-[12px]`} />
                                    </span>
                                    <div className="text-left min-w-0">
                                        <div className="text-[11.5px] font-semibold text-[var(--ink)] leading-tight truncate">{a.label}</div>
                                        <div className="font-mono text-[8.5px] tracking-wider text-[var(--muted)] uppercase truncate">{a.meta}</div>
                                    </div>
                                </motion.li>
                            ))}
                        </ul>

                        {/* live counter */}
                        <div className="mt-4 pt-3 border-t border-[var(--border)] flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.14em]">
                            <span className="text-[var(--muted)] flex items-center gap-1.5">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: matter.accent }} />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: matter.accent }} />
                                </span>
                                LexGraph · indexing
                            </span>
                            <span className="text-[var(--ink-soft)]">{matter.artifacts.length} threaded</span>
                        </div>
                    </motion.div>

                    {/* Desktop orbital visualization */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="hidden md:block relative card-paper p-6 md:p-8 aspect-square max-w-[560px] mx-auto overflow-hidden"
                        data-testid="atlas-graph"
                    >
                        {/* matter tabs */}
                        <div className="absolute top-4 left-4 right-4 z-20 flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                            {MATTERS.map((m, i) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setActive(i)}
                                    data-testid={`atlas-tab-${m.id}`}
                                    className="flex-shrink-0 px-2.5 py-1 rounded-full font-mono text-[9.5px] tracking-[0.12em] uppercase transition-all"
                                    style={{
                                        background: active === i ? matter.accent : "var(--cream-2)",
                                        color: active === i ? "#fff" : "var(--ink-soft)",
                                    }}
                                >
                                    {m.chip}
                                </button>
                            ))}
                        </div>

                        {/* Orbits (decorative rings) */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <defs>
                                <radialGradient id="atlasGlow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor={matter.accent} stopOpacity="0.18" />
                                    <stop offset="60%" stopColor={matter.accent} stopOpacity="0.04" />
                                    <stop offset="100%" stopColor={matter.accent} stopOpacity="0" />
                                </radialGradient>
                            </defs>
                            <circle cx="50" cy="50" r="46" fill="url(#atlasGlow)" />
                        </svg>

                        {/* Animated connector lines from each artifact to the centre */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none">
                            {matter.artifacts.map((a, i) => {
                                const rad = (a.angle * Math.PI) / 180;
                                // approx percent-based positions (match the artifact node radius)
                                const cx = 50 + 32 * Math.cos(rad);
                                const cy = 50 + 32 * Math.sin(rad);
                                return (
                                    <motion.line
                                        key={`${matter.id}-${a.id}-line`}
                                        x1="50%" y1="50%"
                                        x2={`${cx}%`} y2={`${cy}%`}
                                        stroke={matter.accent}
                                        strokeWidth="1"
                                        strokeDasharray="3 3"
                                        initial={{ opacity: 0, pathLength: 0 }}
                                        animate={{ opacity: 0.45, pathLength: 1 }}
                                        transition={{ duration: 0.55, delay: 0.18 + i * 0.06 }}
                                    />
                                );
                            })}
                        </svg>

                        {/* Centre matter node */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={matter.id + "-centre"}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.5 }}
                                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                            >
                                <div
                                    className="rounded-2xl px-4 py-3 text-center shadow-[0_22px_55px_-26px_rgba(35,31,35,0.45)]"
                                    style={{ background: matter.accent, color: "#fff" }}
                                >
                                    <div className="font-mono text-[9.5px] tracking-[0.16em] uppercase opacity-80">Matter</div>
                                    <div className="font-display font-semibold text-[15px] leading-tight mt-1">{matter.title}</div>
                                    <div className="font-mono text-[10px] mt-1 opacity-85">{matter.client}</div>
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        {/* Artifact nodes — positioned by percentage so they scale with the square.
                            Cards anchor to the side of the orbit point so long labels don't
                            push outside the container. Position is on the wrapper, scale/opacity
                            on the inner motion div (Motion's transform would otherwise clobber ours). */}
                        {matter.artifacts.map((a, i) => {
                            const rad = (a.angle * Math.PI) / 180;
                            const left = 50 + 32 * Math.cos(rad);
                            const top = 50 + 32 * Math.sin(rad);
                            const cosA = Math.cos(rad);
                            const tx = cosA > 0.4 ? "-100%" : cosA < -0.4 ? "0%" : "-50%";
                            return (
                                <div
                                    key={`${matter.id}-${a.id}`}
                                    className="absolute z-10"
                                    style={{
                                        left: `${left}%`,
                                        top: `${top}%`,
                                        transform: `translate(${tx}, -50%)`,
                                    }}
                                >
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.6 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, delay: 0.25 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                                        className="card-paper px-2.5 py-2 flex items-center gap-2 hover:border-[var(--blue)]/40 transition-colors max-w-[200px]"
                                    >
                                        <span
                                            className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                                            style={{ background: "var(--cream-2)", color: "var(--ink)" }}
                                        >
                                            <i className={`fa-solid ${a.icon} text-[12px]`} />
                                        </span>
                                        <div className="text-left min-w-0">
                                            <div className="text-[11.5px] font-semibold text-[var(--ink)] leading-tight truncate">{a.label}</div>
                                            <div className="font-mono text-[9px] tracking-wider text-[var(--muted)] uppercase truncate">{a.meta}</div>
                                        </div>
                                    </motion.div>
                                </div>
                            );
                        })}

                        {/* Live counter */}
                        <div className="absolute bottom-4 left-4 right-4 z-20 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.14em]">
                            <span className="text-[var(--muted)] flex items-center gap-1.5">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: matter.accent }} />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: matter.accent }} />
                                </span>
                                LexGraph · indexing
                            </span>
                            <span className="text-[var(--ink-soft)]">{matter.artifacts.length} artifacts threaded</span>
                        </div>

                        <style>{`
                            [data-testid="atlas-graph"] line { transition: stroke 0.4s ease; }
                        `}</style>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default AtlasShowcase;
